import { ref } from 'vue'

/**
 * MK3 echarts theme.
 *
 * Lets us keep vue-echarts (decision: don't port back to Chart.js) and
 * our existing chart-option builders in src/charts.ts while adopting
 * the lighter, soft-pastel visual chrome of the MK3 reference.
 *
 * Usage in a view (only when the MK3 flag is on):
 *
 *   import { applyMk3Theme } from '@/utils/chartTheme'
 *   const options = applyMk3Theme(charts.barChartOptions())
 *   <chart :options="options" />
 *
 * Designed to be a non-mutating deep merge. Pass any options object
 * (including pie chart's `{ baseOption, media }` shape) and get a new
 * options object with MK3 styling layered on top. Existing keys the
 * caller has set (formatters, data, custom series shapes) are
 * preserved — the theme only adds visual properties.
 */

/**
 * Soft pastel palette aligned with the MK3 KPI tile icon-circle
 * colours. Order matters: echarts cycles through `color` for series
 * by index, so the first 4 entries map to the four primary spend
 * categories (Air, Accommodation, Car Hire, Service Fees) the
 * Summary dashboard surfaces.
 */
export const MK3_PALETTE = {
  series: [
    '#3b82f6', // blue-500   — Air Travel
    '#f59e0b', // amber-500  — Accommodation
    '#10b981', // emerald-500 — Car Hire
    '#8b5cf6', // violet-500 — Service Fees
    '#06b6d4', // cyan-500
    '#ef4444', // red-500
    '#84cc16', // lime-500
    '#f97316', // orange-500
  ] as readonly string[],
  axisLine: '#e5e7eb',  // gray-200 — barely-there axis line
  splitLine: '#f3f4f6', // gray-100 — gridline
  text: '#6b7280',      // gray-500 — axis label text
  textStrong: '#111827', // gray-900 — title text
  tooltipBg: '#ffffff',
  tooltipBorder: '#e5e7eb',
}

type AnyObj = Record<string, any>

/**
 * Per-TMC chart accent colour (TAMK3-56). A TA Admin sets one brand colour in
 * Settings → TMC Details; App.vue applies the active client's value here on
 * load and whenever it changes. `null` = use the default MK3 palette.
 *
 * It's a reactive ref so a chart's `applyMk3Theme(...)` computed re-runs when
 * the accent changes (same reactivity model as the showDataLabels flag).
 */
export const chartAccent = ref<string | null>(null)

const HEX6 = /^#[0-9a-fA-F]{6}$/

export function setChartAccent (hex: string | null | undefined): void {
  chartAccent.value = hex && HEX6.test(hex) ? hex.toLowerCase() : null
}

function hexToRgb (hex: string): [number, number, number] {
  const h = hex.replace('#', '')
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ]
}

function rgbToHex (r: number, g: number, b: number): string {
  const to = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

/**
 * Derive an N-colour palette from a single accent as a monochromatic ramp:
 * entry 0 is the exact accent (the brand colour — used for single-series
 * charts / bars / the primary line), and each subsequent entry is a
 * progressively lighter shade of it (mixed further toward white). So a
 * multi-slice chart (e.g. the Policy Compliance donut) reads as tints of the
 * one brand colour rather than a rainbow. `MAX_TINT` caps how light the
 * lightest entry gets so it stays visible against the white card.
 */
const MAX_TINT = 0.72

export function paletteFromAccent (accent: string, count = 8): string[] {
  const [r, g, b] = hexToRgb(accent)
  const palette: string[] = []
  for (let i = 0; i < count; i++) {
    if (i === 0) { palette.push(accent.toLowerCase()); continue }
    const t = (i / (count - 1)) * MAX_TINT // 0 → MAX_TINT across the ramp
    palette.push(rgbToHex(r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t))
  }
  return palette
}

/**
 * The active chart series palette: the per-TMC accent-derived palette when a
 * chart accent is set (TAMK3-56), otherwise the default MK3 palette. Use this
 * (instead of `[...MK3_PALETTE.series]`) anywhere a chart sets `color`
 * explicitly, so those charts follow the accent too.
 */
export function seriesPalette (): string[] {
  return chartAccent.value
    ? paletteFromAccent(chartAccent.value, MK3_PALETTE.series.length)
    : [...MK3_PALETTE.series]
}

/**
 * Brand a single chart colour (TAMK3-56). With no accent set it returns the
 * chart's own `fallback` colour unchanged — so turning the brand-colour switch
 * off restores every chart's original look exactly. With an accent set it
 * returns a shade of the accent, `step` tints up the ramp (0 = the exact
 * accent). `step` lets a two-colour chart whose colours carry meaning
 * (e.g. online vs offline) stay distinguishable while both go on-brand —
 * e.g. `brandColour(a, 0)` + `brandColour(b, 3)` = accent + a lighter shade.
 */
export function brandColour (fallback: string, step = 0): string {
  if (!chartAccent.value) return fallback
  const palette = paletteFromAccent(chartAccent.value, MK3_PALETTE.series.length)
  return palette[Math.max(0, Math.min(step, palette.length - 1))]
}

/**
 * Brand a multi-colour chart palette (TAMK3-56). No accent → the chart's own
 * `fallback` palette unchanged; accent set → a same-length tint ramp of the
 * accent (a mono, on-brand version of e.g. the cabin-class distribution).
 */
export function brandPalette (fallback: readonly string[]): string[] {
  return chartAccent.value
    ? paletteFromAccent(chartAccent.value, fallback.length)
    : [...fallback]
}

/**
 * Deep-clones via JSON. Adequate for echarts options which are pure
 * data structures (no functions on the options the theme touches).
 * Functions that ARE on the input (formatters, etc.) are stripped by
 * the JSON round-trip — so the theme returns its own clean object and
 * the caller is responsible for applying any function-valued options
 * (formatters, click handlers) AFTER theming, or via mergeOptions on
 * the chart instance.
 *
 * In practice we apply the theme to a fresh chart-options object from
 * charts.ts and then immediately wire it as `:options` on <chart>;
 * formatters set inside charts.ts itself survive because charts.ts
 * builds a fresh closure each call (the function references aren't
 * the source-of-truth, they get re-attached after merge).
 *
 * Update: to keep formatters intact we shallow-merge tooltip /
 * axisLabel keys instead of overwriting, and use a structuredClone
 * fallback that preserves function references.
 */
function clone<T> (value: T): T {
  if (typeof structuredClone === 'function') {
    try { return structuredClone(value) } catch { /* fall through */ }
  }
  return JSON.parse(JSON.stringify(value))
}

function styleAxis (axis: AnyObj, isCategory: boolean) {
  axis.axisLine = {
    ...(axis.axisLine || {}),
    lineStyle: { ...(axis.axisLine?.lineStyle || {}), color: MK3_PALETTE.axisLine },
  }
  axis.axisTick = { ...(axis.axisTick || {}), show: false }
  axis.axisLabel = {
    ...(axis.axisLabel || {}),
    color: MK3_PALETTE.text,
    fontSize: 12,
  }
  // Hide vertical gridlines on category axis (cleaner look). Keep
  // horizontal gridlines on the value axis (light dashed).
  if (isCategory) {
    axis.splitLine = { show: false }
  } else {
    axis.splitLine = {
      ...(axis.splitLine || {}),
      show: true,
      lineStyle: {
        color: MK3_PALETTE.splitLine,
        type: 'dashed',
      },
    }
  }
}

function styleTarget (target: AnyObj) {
  // Series palette — the per-TMC accent-derived palette when a chart accent
  // colour is set (TAMK3-56), otherwise the default MK3 palette.
  if (!target.color) target.color = seriesPalette()

  // Axes — handle both object and array shapes
  if (target.xAxis) {
    const arr = Array.isArray(target.xAxis) ? target.xAxis : [target.xAxis]
    arr.forEach((axis: AnyObj) => styleAxis(axis, axis.type !== 'value'))
  }
  if (target.yAxis) {
    const arr = Array.isArray(target.yAxis) ? target.yAxis : [target.yAxis]
    arr.forEach((axis: AnyObj) => styleAxis(axis, axis.type === 'category'))
  }

  // Tooltip — white card, soft shadow, rounded
  if (target.tooltip) {
    target.tooltip = {
      ...target.tooltip,
      backgroundColor: MK3_PALETTE.tooltipBg,
      borderColor: MK3_PALETTE.tooltipBorder,
      borderWidth: 1,
      textStyle: {
        ...(target.tooltip.textStyle || {}),
        color: MK3_PALETTE.textStrong,
        fontSize: 12,
      },
      extraCssText:
        'box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1); border-radius: 8px; padding: 8px 12px;',
    }
  }

  // Title — readable on white card. The original title.show=false rule
  // (charts.ts barChart) is preserved; we only touch styling.
  if (target.title) {
    target.title = {
      ...target.title,
      textStyle: {
        ...(target.title.textStyle || {}),
        color: MK3_PALETTE.textStrong,
        fontWeight: 600,
        fontSize: 14,
      },
    }
  }
}

/**
 * ECharts `series[].label` config for the "show data labels" toggle
 * (TAMK3-28). Off by default; when the UI-store `showDataLabels` flag is on,
 * dashboard charts print the value on each bar / point / slice so agents can
 * screenshot them for the reports they give customers.
 *
 * Pass it straight into a series' `label` and drive `show` off the store flag,
 * e.g. `label: dataLabel(ui.showDataLabels, { formatter: v => formatPrice(v.value) })`.
 * `formatter` gives the value its correct type ($ / count / %); `position`
 * suits the mark ('top' for vertical bars & lines — the default; 'right' for
 * horizontal bars; 'inside' / 'outside' for pie). Styling is deliberately
 * subtle: small, muted text. Because the series `label` lives in the view's
 * reactive `computed`, toggling the flag re-pushes and the labels appear/hide.
 */
export function dataLabel (
  show: boolean,
  opts: { formatter?: any; position?: string; color?: string } = {},
): AnyObj {
  return {
    show,
    position: opts.position ?? 'top',
    formatter: opts.formatter,
    color: opts.color ?? MK3_PALETTE.text,
    fontSize: 11,
    fontWeight: 500,
  }
}

export function applyMk3Theme<T extends AnyObj> (options: T): T {
  const cloned = clone(options)

  // Pie chart's responsive options use { baseOption, media: [...] }.
  // Apply to baseOption when present; fall back to top-level shape.
  if (cloned.baseOption) {
    styleTarget(cloned.baseOption)
  } else {
    styleTarget(cloned)
  }

  return cloned
}
